import React, { forwardRef } from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';

const AlternativeArrangement = forwardRef(({ leaveData, substitutions, schoolName = "Vidya Pratishthan's Supe English Medium School" }, ref) => {
    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    const teacherName = leaveData?.staff?.user
        ? `${leaveData.staff.user.firstName} ${leaveData.staff.user.lastName}`
        : 'N/A';

    return (
        <Box ref={ref} sx={{ p: 3, fontFamily: 'Arial, sans-serif', fontSize: '12px', maxWidth: '800px', margin: 'auto' }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                    {schoolName}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1 }}>
                    ALTERNATIVE ARRANGEMENT FOR LECTURES
                </Typography>
            </Box>

            {/* Teacher Info */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ mb: 1 }}>
                    <Typography><strong>Teacher Name:</strong> {teacherName}</Typography>
                    <Typography><strong>Leave Type:</strong> {leaveData?.leaveType || 'N/A'}</Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                    <Typography><strong>From:</strong> {formatDate(leaveData?.startDate)}</Typography>
                    <Typography><strong>To:</strong> {formatDate(leaveData?.endDate)}</Typography>
                </Box>
                <Box>
                    <Typography><strong>Total Days:</strong> {leaveData?.totalDays || 0}</Typography>
                    <Typography><strong>Date:</strong> {formatDate(new Date())}</Typography>
                </Box>
            </Box>

            {/* Substitution Table */}
            <Table size="small" sx={{ border: '1px solid #000', '& td, & th': { border: '1px solid #000', padding: '6px 8px' } }}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Sr.</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Period No.</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Class & Div</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Substitute Teacher</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Signature</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {substitutions && substitutions.length > 0 ? (
                        substitutions.map((sub, index) => (
                            <TableRow key={sub.id || index}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>{formatDate(sub.leaveDate)}</TableCell>
                                <TableCell>{sub.periodNumber}</TableCell>
                                <TableCell>{sub.periodTime || '-'}</TableCell>
                                <TableCell>
                                    {sub.class?.name || ''} {sub.section?.name || ''}
                                </TableCell>
                                <TableCell>{sub.subject?.name || '-'}</TableCell>
                                <TableCell>
                                    {sub.substituteTeacher?.user
                                        ? `${sub.substituteTeacher.user.firstName} ${sub.substituteTeacher.user.lastName}`
                                        : sub.substituteConfirmed ? 'Confirmed' : 'Pending'
                                    }
                                </TableCell>
                                <TableCell sx={{ minWidth: '80px' }}></TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 2 }}>
                                No substitutions assigned
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            {/* Reason */}
            <Box sx={{ mt: 2, mb: 3 }}>
                <Typography><strong>Reason for Leave:</strong> {leaveData?.reason || 'N/A'}</Typography>
            </Box>

            {/* Signature Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3 }}>
                <Box sx={{ textAlign: 'center', width: '22%' }}>
                    <Box sx={{ borderTop: '1px solid #000', pt: 1, mt: 4 }}>
                        <Typography variant="caption">Teacher's Sign</Typography>
                    </Box>
                </Box>
                <Box sx={{ textAlign: 'center', width: '22%' }}>
                    <Box sx={{ borderTop: '1px solid #000', pt: 1, mt: 4 }}>
                        <Typography variant="caption">HOD / Supervisor Sign</Typography>
                    </Box>
                </Box>
                <Box sx={{ textAlign: 'center', width: '22%' }}>
                    <Box sx={{ borderTop: '1px solid #000', pt: 1, mt: 4 }}>
                        <Typography variant="caption">Office Remark & Stamp</Typography>
                    </Box>
                </Box>
                <Box sx={{ textAlign: 'center', width: '22%' }}>
                    <Box sx={{ borderTop: '1px solid #000', pt: 1, mt: 4 }}>
                        <Typography variant="caption">Principal Sign</Typography>
                    </Box>
                </Box>
            </Box>

            {/* Footer Notes */}
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed #ccc', fontSize: '10px' }}>
                <Typography variant="caption" display="block">
                    * This form confirms that alternative arrangements have been made for all scheduled lectures.
                </Typography>
                <Typography variant="caption" display="block">
                    * All substitute teachers have confirmed their availability.
                </Typography>
            </Box>
        </Box>
    );
});

AlternativeArrangement.displayName = 'AlternativeArrangement';

export default AlternativeArrangement;
